/**
 * Tiện ích đăng nhập xã hội — Google One Tap và Sign in with Apple.
 *
 * Lazy-load script SDK, cache promise để tránh tải trùng.
 * Client ID lấy từ biến môi trường REACT_APP_*.
 */
import { loadScript } from '../utils/loadScript';

const GOOGLE_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const APPLE_SCRIPT_SRC = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

/** Promise cache — chỉ load script một lần */
let googleScriptPromise;
let appleScriptPromise;

/** Cấu hình client ID từ env — rỗng nếu chưa cấu hình */
export const SOCIAL_AUTH = {
  googleClientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
  appleClientId: process.env.REACT_APP_APPLE_CLIENT_ID || '',
};

/** Kiểm tra Google OAuth đã được cấu hình chưa */
export const isGoogleAuthEnabled = () => Boolean(SOCIAL_AUTH.googleClientId);

/** Kiểm tra Apple OAuth đã được cấu hình chưa */
export const isAppleAuthEnabled = () => Boolean(SOCIAL_AUTH.appleClientId);

/**
 * Tải Google Identity Services script (nếu chưa có).
 * @returns {Promise<void>}
 */
export const loadGoogleScript = () => {
  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }

  if (!googleScriptPromise) {
    googleScriptPromise = loadScript(GOOGLE_SCRIPT_SRC);
  }

  return googleScriptPromise;
};

/**
 * Tải Apple Sign In script (nếu chưa có).
 * @returns {Promise<void>}
 */
export const loadAppleScript = () => {
  if (window.AppleID?.auth) {
    return Promise.resolve();
  }

  if (!appleScriptPromise) {
    appleScriptPromise = loadScript(APPLE_SCRIPT_SRC);
  }

  return appleScriptPromise;
};

/**
 * Khởi tạo Apple Auth SDK với popup mode.
 * @returns {Promise<boolean>} true nếu khởi tạo thành công
 */
export const initializeAppleAuth = async () => {
  if (!isAppleAuthEnabled()) {
    return false;
  }

  await loadAppleScript();

  window.AppleID.auth.init({
    clientId: SOCIAL_AUTH.appleClientId,
    scope: 'name email',
    redirectURI: window.location.origin,
    usePopup: true,
  });

  return true;
};

/**
 * Mở popup đăng nhập Apple và trả về idToken + tên (nếu có).
 * @returns {Promise<{ idToken: string, name: string|null }>}
 */
export const signInWithApple = async () => {
  await initializeAppleAuth();
  const response = await window.AppleID.auth.signIn();
  const idToken = response?.authorization?.id_token;

  if (!idToken) {
    throw new Error('Apple token missing');
  }

  const firstName = response?.user?.name?.firstName || '';
  const lastName = response?.user?.name?.lastName || '';
  const name = `${firstName} ${lastName}`.trim();

  return {
    idToken,
    name: name || null,
  };
};

/**
 * Render nút Google Sign-In vào DOM element.
 * @param {HTMLElement} element - Container cho nút Google
 * @param {Object} callbacks
 * @param {Function} callbacks.onSuccess - Nhận credential JWT
 * @param {Function} [callbacks.onError]
 */
export const mountGoogleSignInButton = async (element, { onSuccess, onError }) => {
  if (!isGoogleAuthEnabled() || !element) {
    return;
  }

  await loadGoogleScript();

  element.innerHTML = '';

  window.google.accounts.id.initialize({
    client_id: SOCIAL_AUTH.googleClientId,
    callback: (response) => {
      if (response?.credential) {
        onSuccess?.(response.credential);
        return;
      }

      onError?.();
    },
  });

  window.google.accounts.id.renderButton(element, {
    type: 'icon',
    theme: 'outline',
    shape: 'rectangular',
    size: 'large',
    width: element.offsetWidth || 240,
  });
};
